<script setup lang="ts">
import { ref, provide, onMounted } from 'vue'
import DashboardSidebar from '../components/dashboard/DashboardSidebar.vue'
import DashboardHeader from '../components/dashboard/DashboardHeader.vue'
import { useNotification } from '@/composables/useNotification'
import AppNotification from '@/components/common/AppNotification.vue'
import { useAppUpdates } from '../composables/useAppUpdates'
import UpdateModal from '../components/updates/UpdateModal.vue'
import { useTheme } from '../composables/useTheme'
import LanguageSelector from '@/components/language/LanguageSelector.vue'
import { useRoute } from 'vue-router'
import GlobalEmailComposer from '@/components/email/GlobalEmailComposer.vue'

const isSidebarOpen = ref(true)
const sidebarRef = ref()
const route = useRoute()
const { theme, toggleTheme } = useTheme()

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const handleThemeToggle = () => {
  sidebarRef.value?.handleThemeToggle()
}

// Create an injection key for company changes
const COMPANY_CHANGED_KEY = Symbol('companyChanged')

// Provide the company change event to all child components
provide(COMPANY_CHANGED_KEY, (companyId: string) => {
  // This function will be injected into child components
  // They can use it to reload their data when the company changes
})

const handleCompanyChanged = (companyId: string) => {
  // Emit the company change event through the provide/inject system
  provide(COMPANY_CHANGED_KEY, companyId)
}

const { notifications, removeNotification } = useNotification()

const { update, showUpdateModal, getUserVersion, markUpdateAsViewed } = useAppUpdates()

onMounted(async () => {
  await getUserVersion()
})
</script>

<template>
  <div class="min-h-screen bg-secondary-100 dark:bg-secondary-900">
    <!-- Sidebar -->
    <DashboardSidebar 
      ref="sidebarRef"
      :is-open="isSidebarOpen" 
      :current-theme="theme"
      @toggle="toggleSidebar"
    />

    <!-- Main Content -->
    <div 
      class="transition-all duration-300"
      :class="[
        isSidebarOpen ? 'lg:pl-64' : 'lg:pl-20'
      ]"
    >
      <DashboardHeader 
        :isSidebarCollapsed="!isSidebarOpen"
        @toggle-sidebar="toggleSidebar"
        @company-changed="handleCompanyChanged"
        @theme-toggle="handleThemeToggle"
      >
        <template v-if="route.path === '/login' || route.path === '/signup'" #actions>
          <LanguageSelector />
        </template>
      </DashboardHeader>
      
      <main class="p-4 md:p-8 flex-1 overflow-auto" style="height: calc(100vh - 64px);">
        <RouterView />
      </main>
    </div>

    <!-- Muelle de compositores de correo: sobrevive a la navegación (fuera de <main>) -->
    <GlobalEmailComposer />

    <!-- Notificaciones -->
    <div class="fixed top-4 right-4 z-[70] space-y-4">
      <AppNotification
        v-for="notification in notifications"
        :key="notification.id"
        :type="notification.type"
        :title="notification.title"
        :message="notification.message"
        :auto-close="true"
        :duration="5000"
        @close="removeNotification(notification.id)"
      />
    </div>


    <UpdateModal
      v-if="update"
      :is-open="showUpdateModal"
      :update="update"
      @close="markUpdateAsViewed"
    />
  </div>
</template>